import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  Twitter,
  Linkedin,
  ArrowRight,
  Briefcase,
  Mail,
  CheckCircle,
  AlertCircle,
  Sprout,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { TerraMatrixBrand } from "@/components/branding/TerraMatrixBrand";
import { useResponsiveInfo } from "@/hooks/useResponsiveViewport";
import { SignInModal } from "@/components/auth/SignInModal";
import { PilotContactModal, ThankYouModal } from "@/components/pilot";
import { newsletterService } from "@/services/newsletterService";
import { SEO } from "@/components/seo/SEO";

// ============================================================================
// DESIGN TOKENS (Style Guide Colors)
// ============================================================================
const colors = {
  primary: "hsl(var(--primary))",
  primaryHover: "hsl(var(--primary) / 0.9)",
  secondary: "#B2AC88", // Sage Green
  seafoam: "#93E9BE",
  blueGrotto: "#116B98",
  desertSun: "#E27D3D",
  charcoal: "#0A0A0A",
  midGray: "#6E6E6E",
  lightGray: "#F4F5F4",
  champagne: "#F7E7CE",
  white: "#FFFFFF",
};

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

// ============================================================================
// DATA
// ============================================================================
const footerLinks = {
  product: [
    { label: "Features", href: "/#features" },
    { label: "Solutions", href: "/#industries" },
    { label: "Pricing", href: "#" },
    { label: "Documentation", href: "#" },
  ],
  company: [
    { label: "About", href: "/about" },
    { label: "Blog", href: "/blog" },
    { label: "Careers", href: "/careers" },
    { label: "Contact", href: "/contact" },
  ],
  legal: [
    { label: "Privacy Policy", href: "#" },
    { label: "Terms of Service", href: "#" },
    { label: "Cookie Policy", href: "#" },
  ],
};

// ============================================================================
// NAVIGATION COMPONENT
// ============================================================================
interface NavigationProps {
  scrolled: boolean;
  onContactClick: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ scrolled, onContactClick }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [signInModalOpen, setSignInModalOpen] = useState(false);
  const { isMobile } = useResponsiveInfo();

  const handleSignInClick = () => {
    if (isMobile) {
      navigate("/login");
    } else {
      setSignInModalOpen(true);
    }
    setMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    setMobileMenuOpen(false);
    onContactClick();
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-md shadow-sm" : "bg-transparent"
      }`}
      style={{
        backgroundColor: scrolled ? "rgba(247, 231, 206, 0.95)" : "transparent",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[60px]">
          {/* Logo - Left */}
          <div className="flex-1 flex items-center">
            <a href="/" className="flex-shrink-0">
              <TerraMatrixBrand
                markClassName="h-8 w-8 md:h-10 md:w-10"
                nameClassName={
                  scrolled
                    ? "text-xl md:text-2xl text-gray-950"
                    : "text-xl md:text-2xl text-white"
                }
                className="transition-all duration-300"
              />
            </a>
          </div>

          {/* Desktop Navigation - Center */}
          <div className="hidden md:flex items-center justify-center gap-6">
            <Button
              variant="ghost"
              className={`font-medium transition-colors duration-200 ${
                scrolled ? "hover:bg-gray-100" : "hover:bg-white/20"
              }`}
              style={{
                color: scrolled ? colors.midGray : colors.white,
              }}
              onClick={() => navigate("/")}
            >
              Home
            </Button>
            <Button
              variant="ghost"
              className={`font-medium transition-colors duration-200 ${
                scrolled ? "hover:bg-gray-100" : "hover:bg-white/20"
              }`}
              style={{
                color: scrolled ? colors.midGray : colors.white,
              }}
              onClick={() => navigate("/about")}
            >
              About
            </Button>
            <Button
              variant="ghost"
              className={`font-medium transition-colors duration-200 ${
                scrolled ? "hover:bg-gray-100" : "hover:bg-white/20"
              }`}
              style={{
                color: scrolled ? colors.midGray : colors.white,
              }}
              onClick={handleContactClick}
            >
              Contact
            </Button>
          </div>

          {/* Desktop Sign In - Right */}
          <div className="flex-1 hidden md:flex items-center justify-end">
            <Button
              className="font-medium rounded transition-all duration-200 bg-primary hover:bg-primary/90 text-primary-foreground"
              onClick={handleSignInClick}
            >
              Sign In
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <X className={scrolled ? "text-gray-900" : "text-white"} size={24} />
            ) : (
              <Menu className={scrolled ? "text-gray-900" : "text-white"} size={24} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden bg-white rounded-lg shadow-lg p-4 mb-4"
          >
            <div className="flex flex-col gap-3">
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/");
                }}
              >
                Home
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={() => {
                  setMobileMenuOpen(false);
                  navigate("/about");
                }}
              >
                About
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={handleContactClick}
              >
                Contact
              </Button>
              <hr className="border-gray-200" />
              <Button
                variant="ghost"
                className="w-full justify-start transition-colors duration-200"
                style={{ color: colors.charcoal }}
                onClick={handleSignInClick}
              >
                Sign In
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Sign In Modal */}
      <SignInModal isOpen={signInModalOpen} onClose={() => setSignInModalOpen(false)} />
    </nav>
  );
};

// ============================================================================
// HERO SECTION
// ============================================================================
const HeroSection: React.FC = () => {
  return (
    <section
      className="relative min-h-[60vh] flex items-center justify-center overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.charcoal} 100%)`,
      }}
    >
      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="text-center max-w-4xl mx-auto"
        >
          <motion.div
            variants={fadeInUp}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
            style={{ backgroundColor: `${colors.seafoam}30` }}
          >
            <Sprout className="h-4 w-4" style={{ color: colors.seafoam }} />
            <span className="text-sm font-medium" style={{ color: colors.seafoam }}>
              Join Our Mission
            </span>
          </motion.div>

          <motion.h1
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6"
          >
            Build the Future of Agriculture
          </motion.h1>

          <motion.p
            variants={fadeInUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed"
          >
            Join a team that's transforming how farms, breweries, and restaurants
            operate. Help us bring AI-powered automation to real-world infrastructure.
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// OPEN POSITIONS SECTION
// ============================================================================
const OpenPositionsSection: React.FC = () => {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setStatus("error");
      setMessage("Please enter your email address");
      return;
    }

    setStatus("loading");

    try {
      const result = await newsletterService.subscribe(email);
      if (result.success) {
        setStatus("success");
        setMessage(result.message || "You're subscribed! We'll notify you when positions open.");
        setEmail("");
      } else {
        setStatus("error");
        setMessage(result.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setMessage("Something went wrong. Please try again.");
    }
  };

  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.lightGray }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="text-center"
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: colors.charcoal }}
          >
            Open Positions
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg mb-12 max-w-2xl mx-auto"
            style={{ color: colors.midGray }}
          >
            We're building something special. Check back for opportunities to join our team.
          </motion.p>

          {/* Empty State Card */}
          <motion.div
            variants={fadeInUp}
            className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-gray-100"
          >
            <div
              className="w-20 h-20 mx-auto rounded-2xl flex items-center justify-center mb-6"
              style={{ backgroundColor: `${colors.primary}10` }}
            >
              <Briefcase className="h-10 w-10" style={{ color: colors.primary }} />
            </div>

            <h3
              className="text-xl md:text-2xl font-semibold mb-3"
              style={{ color: colors.charcoal }}
            >
              No Open Positions Right Now
            </h3>

            <p
              className="text-base mb-8 max-w-md mx-auto leading-relaxed"
              style={{ color: colors.midGray }}
            >
              We're not actively hiring at the moment, but we're always looking for
              exceptional talent. Sign up to be notified when positions open.
            </p>

            {/* Job Alert Signup Form */}
            <div className="max-w-md mx-auto">
              <div
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-6"
                style={{ backgroundColor: `${colors.blueGrotto}15` }}
              >
                <Mail className="h-4 w-4" style={{ color: colors.blueGrotto }} />
                <span className="text-sm font-medium" style={{ color: colors.blueGrotto }}>
                  Get Job Alerts
                </span>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  disabled={status === "loading" || status === "success"}
                  className="flex-1 px-4 py-3 rounded-lg border border-input focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  aria-label="Email address for job alerts"
                />
                <Button
                  type="submit"
                  disabled={status === "loading" || status === "success"}
                  className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {status === "loading" ? "Subscribing..." : status === "success" ? "Subscribed!" : "Notify Me"}
                </Button>
              </form>

              {/* Status Message */}
              {(status === "success" || status === "error") && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex items-center justify-center gap-2 mt-4 text-sm ${
                    status === "success" ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {status === "success" ? (
                    <CheckCircle size={16} />
                  ) : (
                    <AlertCircle size={16} />
                  )}
                  {message}
                </motion.div>
              )}

              <p className="text-sm mt-4" style={{ color: colors.midGray }}>
                We'll only email you about job opportunities. Unsubscribe anytime.
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// CTA SECTION
// ============================================================================
interface CTASectionProps {
  onContactClick: () => void;
}

const CTASection: React.FC<CTASectionProps> = ({ onContactClick }) => {
  return (
    <section className="py-20 md:py-28" style={{ backgroundColor: colors.primary }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
          >
            Have Questions?
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
          >
            Want to learn more about TerraMatrix or discuss potential opportunities?
            We'd love to hear from you.
          </motion.p>
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              size="lg"
              className="text-base font-semibold min-w-[180px] px-8 py-6 rounded border-2 shadow-lg hover:shadow-xl bg-background hover:bg-accent border-background hover:border-accent text-primary transition-all duration-300 hover:scale-105"
              onClick={onContactClick}
            >
              Contact Us
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============================================================================
// FOOTER SECTION
// ============================================================================
const FooterSection: React.FC = () => {
  return (
    <footer className="py-16 md:py-20 text-white" style={{ backgroundColor: colors.charcoal }}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <TerraMatrixBrand
              markClassName="h-10 w-10"
              nameClassName="text-3xl text-white"
              className="mb-6"
            />
            <p className="text-gray-400 max-w-sm leading-relaxed mb-6">
              The operating system for real-world infrastructure. Monitor, automate, and optimize
              your operations with AI-powered IoT.
            </p>
            <div className="flex gap-4">
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="Twitter"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center hover:bg-gray-700 transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Product Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Product
            </h3>
            <ul className="space-y-3">
              {footerLinks.product.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Company
            </h3>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wider text-gray-400 mb-4">
              Legal
            </h3>
            <ul className="space-y-3">
              {footerLinks.legal.map((link) => (
                <li key={link.label}>
                  <a href={link.href} className="text-gray-300 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            &copy; {new Date().getFullYear()} TerraMatrix. All rights reserved.
          </p>
          <p className="text-gray-500 text-sm">Built with precision for real-world operations.</p>
        </div>
      </div>
    </footer>
  );
};

// ============================================================================
// MAIN CAREERS PAGE COMPONENT
// ============================================================================
export const CareersPage: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsiveInfo();

  // Scroll state
  const [scrolled, setScrolled] = useState(false);

  // Modal states
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [thankYouModalOpen, setThankYouModalOpen] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Handle contact button click
  const handleContactClick = () => {
    if (isMobile) {
      navigate("/contact");
    } else {
      setContactModalOpen(true);
    }
  };

  // Handle successful form submission
  const handleContactSuccess = (email: string) => {
    setSubmittedEmail(email);
    setContactModalOpen(false);
    setThankYouModalOpen(true);
  };

  // Handle thank you modal close
  const handleThankYouClose = () => {
    setThankYouModalOpen(false);
    setSubmittedEmail("");
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Careers at TerraMatrix - Join Our Team"
        description="Join TerraMatrix in transforming agriculture, brewing, and restaurant operations with AI-powered IoT. Explore career opportunities and be part of building the future of industrial automation."
        canonical="/careers"
        keywords="terramatrix careers, IoT jobs, agriculture technology careers, automation engineering jobs, startup opportunities"
      />
      {/* Skip link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-background focus:text-primary focus:rounded focus:shadow-lg focus:font-medium"
      >
        Skip to main content
      </a>

      <Navigation scrolled={scrolled} onContactClick={handleContactClick} />

      <main id="main-content">
        <HeroSection />
        <OpenPositionsSection />
        <CTASection onContactClick={handleContactClick} />
      </main>

      <FooterSection />

      {/* Contact Modal */}
      <PilotContactModal
        isOpen={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        onSuccess={handleContactSuccess}
      />

      {/* Thank You Modal */}
      <ThankYouModal
        isOpen={thankYouModalOpen}
        onClose={handleThankYouClose}
        email={submittedEmail}
      />
    </div>
  );
};
